/**
 * !! IMPORTANT !!
 *
 * This entire file is basically just a COPY/PASTE  from BCATS ui portion (Refer to ReadMe).
 * So, implementation change of these functions may also need to be relfected in BCATS.
 * Ex. adding a new column
 *
 * In the future, we want to move this into a shared utility library
 * Since we should be trying to keep the interfaces of our bulk uploader components the same.
 *
 * The creation of this utility space will have to be done in the future.
 */
import _ from 'lodash';
import XLSX from 'xlsx';
import { MAX_RANGE_LIMIT, valueHasPAN } from 'Utils';

// ripped off of BCATS
const requiredHeadersAsStringArray = (acc: string[][], curr: BulkUploaderColumn) => {
    if (curr?.required) {
        if (!curr?.acceptableHeaders) return acc;
        if (Array.isArray(curr?.acceptableHeaders)) {
            return acc.concat([curr?.acceptableHeaders?.map((accepatableHeader) => accepatableHeader.toLowerCase())]);
        }
        return acc.concat([[curr?.acceptableHeaders]]);
    }
    return acc;
};

// ripped off of BCATS
function getHeaderRow(sheet: any): string[] {
    const headers = [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const R = range.s.r; /* start in the first row */

    let C;
    /* walk every column in the range */
    for (C = range.s.c; C <= range.e.c; ++C) {
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]; /* find the cell in the first row */

        let hdr = `UNKNOWN ${C}`; // <-- replace with your desired default
        if (cell && cell.t) hdr = XLSX.utils.format_cell(cell);

        headers.push(hdr);
    }
    return headers;
}

// ripped off of BCATS
// TODO: move this back over to StencilJS BCATS project
export function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // eslint-disable-next-line
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const lowercaseHeaders = (workSheet: XLSX.WorkSheet, columns: BulkUploaderColumn[]) => {
    const workSheetHeaders = getHeaderRow(workSheet);
    const header = workSheetHeaders
        // we don't care about casing for headers
        .map((v) => v.toLowerCase())
        .map((singleHeader) => {
            const v = columns.find((o) => o?.acceptableHeaders?.includes(singleHeader));
            if (v?.accessor == null) {
                return null;
            }
            return v.accessor;
        });
    return header as string[];
};

// TODO: move this to BCATS
export const validateCsvHeaders = (workSheet: XLSX.WorkSheet, columns: BulkUploaderColumn[]) => {
    const requiredColumns = columns.reduce(requiredHeadersAsStringArray, []);
    // workSheet headers is [a, b, c, d]
    const workSheetHeaders = getHeaderRow(workSheet);
    const header = lowercaseHeaders(workSheet, columns);
    const hasAtLeastOneHeader = !_.every(header, (v) => v === null);
    // assume user has given us tiny file so reading should be okay
    const workSheetAsJson = XLSX.utils
        // raw false converts all data to string
        .sheet_to_json(workSheet, { defval: '', header, raw: false })
        // strip out the header row
        ?.slice(1);
    const lowercaseColumnsArray: string[] = workSheetHeaders.map((v) => v.toLowerCase()) as string[];
    const difference = requiredColumns.filter((requiredHeader) => {
        // for the current required headers, there should be some value
        // inside of columnsArray that maps to it. If not, this "difference" in required headers and
        // the csv headers means a required header is missing.
        return !lowercaseColumnsArray.some((b) => requiredHeader.includes(b as any));
    });
    if (!workSheetAsJson.length && hasAtLeastOneHeader) {
        return {
            difference: requiredColumns,
            validationMessage: 'Include at least one row of data'
        };
    }
    return {
        difference,
        validationMessage: `Missing required column(s): ${difference.map((v) => v?.join(' or ')).join(', ')}`
    };
};

// TODO: move over to BCATS
export const getWorkSheetJson = (workSheet: XLSX.WorkSheet, columns: BulkUploaderColumn[]) => {
    // requiredColumns is [[a, b], [c, d]];
    const workSheetAsJson = XLSX.utils
        // raw false converts all data to string
        .sheet_to_json(workSheet, { defval: '', header: lowercaseHeaders(workSheet, columns), raw: false })
        // strip out the header row
        ?.slice(1);
    // add a rowNum for api -- don't do a map because we don't want to create another array in memory
    // additionally, since we are including the header, xlsx counts this as a data row
    // so we need to increment the row count by one
    workSheetAsJson.forEach((o: any) => {
        o.rowNum = o.__rowNum__ + 1;
    });
    const lowercaseColumnsArray: string[] = getHeaderRow(workSheet).map((v) => v.toLowerCase()) as string[];
    // fall through -- difference should be empty array
    // validationMessage should not be needed but just in case include it.
    return {
        columns: lowercaseColumnsArray,
        workSheetAsJson
    };
};

// this has to happen after we've already processed and replaced the column header
// with the accessor --> otherwise throw an error
export const getColumnsWithValueCount = (rows: any[], configColumns: BulkUploaderColumn[]): BulkUploaderColumn[] => {
    const __COUNT__ = '__count__';
    const DEFAULT = 'default';

    if (!Array.isArray(configColumns)) {
        throw new Error('expected config columns to be an array');
    }
    const updatedConfig = configColumns?.map((o: any) => {
        if (!o?.valuesToCount?.length) return o;

        // setup the config object with a counter map
        o[__COUNT__] = {};
        o[__COUNT__][DEFAULT] = 0;

        o.valuesToCount.forEach((valueToTrack: any) => {
            rows.forEach((row) => {
                if (!Object.prototype.hasOwnProperty.call(row, o.accessor)) {
                    throw new Error(`item does not have property ${o.accessor}`);
                }

                if (o[__COUNT__][valueToTrack] == null) o[__COUNT__][valueToTrack] = 0;

                if (row[o.accessor] === valueToTrack) {
                    o[__COUNT__][valueToTrack] += 1;
                } else {
                    o[__COUNT__][DEFAULT] += 1;
                }
            });
        });
        return o;
    });
    return updatedConfig;
};

export const getEntriesCount = (columns: BulkUploaderColumn[], accessor = 'action') => {
    return columns?.find((o) => o?.accessor === accessor)?.__count__?.default;
};

// utc date format should meet EMPTY_OR_UTC_DATE_REGEX
// Note: JavaScript Date library automatically converts Feb 30 to Mar 1 and Feb 31 to Mar 2
// We want to explictly check if timestamp of the csvTimeField stays the same after converting it to ISOString
export const isValidDate = (csvTimeField: string) => {
    try {
        const dateComponents = csvTimeField.split(/[\s-:]/);
        const dateObjectUTC = new Date(
            Date.UTC(
                parseInt(dateComponents[0], 10),
                parseInt(dateComponents[1], 10) - 1,
                parseInt(dateComponents[2], 10),
                parseInt(dateComponents[3], 10),
                parseInt(dateComponents[4], 10)
            )
        );

        const dateIsoString = dateObjectUTC.toISOString();

        const csvTimeFieldYear = dateIsoString.substring(0, 4);
        const csvTimeFieldMonth = dateIsoString.substring(5, 7);
        const csvTimeFieldDay = dateIsoString.substring(8, 10);
        const csvTimeFieldHour = dateIsoString.substring(11, 13);
        const csvTimeFieldMinute = dateIsoString.substring(14, 16);

        return (
            csvTimeFieldYear === dateComponents[0] &&
            csvTimeFieldMonth === dateComponents[1] &&
            csvTimeFieldDay === dateComponents[2] &&
            csvTimeFieldHour === dateComponents[3] &&
            csvTimeFieldMinute === dateComponents[4]
        );
    } catch (e) {
        // if user input is not in the correct format, YYYY-MM-DD HH:MM
        return false;
    }
};

const genErrorString = (stringToTest: string) => (acc: string, test: ValidationObject) => {
    // run each test of validation.tests
    if (test?.method === 'checkHasPAN') {
        const isPAN = valueHasPAN(stringToTest);
        if (isPAN) {
            return `${acc} ${test.message}`;
        }
    }

    if (test?.method === 'isDateValid') {
        // when there's data, then validate. Otherwise, cell can be left empty
        if (stringToTest && !isValidDate(stringToTest)) {
            return `${acc} ${test.message}`;
        }
    }

    const result = String(stringToTest)?.match(test?.regex as RegExp);

    if (!result) {
        return `${acc} ${(test.errMessage ? test.errMessage : test.message) || 'Please enter valid input'}`;
    }

    return acc;
};

export const validateData = (csvJSON: any[], columns: BulkUploaderColumn[]): { Row: Number; Errors: any }[] => {
    const errors: any = {};
    const validators = columns.reduce(
        (acc, col) => ({
            ...acc,
            [String(col.accessor)]: {
                ...col.validation,
                header: col.acceptableHeaders
            }
        }),
        {} as { [key: string]: any }
    );

    // csvJSON is generated by getWorkSheetJson function
    csvJSON.forEach((row) => {
        // keys are equivalent to "accessors" of columns array (case-sensitive)
        // Ref: useListTypeBulkUploaderColumns
        const keys = Object.keys(row);

        if (keys.includes('to') && keys.includes('from')) {
            const from = +row.from;
            const to = +row.to;
            if (from > to) {
                errors[row.rowNum] = 'Ensure "From" value is smaller than "To" value.';
            }
            if (to - from > MAX_RANGE_LIMIT) {
                errors[row.rowNum] = `Range cannot be greater than ${MAX_RANGE_LIMIT}.`;
            }
        }

        if (keys.includes('startTime') && keys.includes('endTime')) {
            const startT = new Date(row.startTime as string);
            const endT = new Date(row.endTime as string);

            // regex is only checking for pattern matching, validate dates more strictly.
            // JavaScript date library validates 2024-02-30 as true.
            if (isValidDate(row.startTime) && isValidDate(row.endTime) && startT > endT) {
                errors[row.rowNum] = `${errors[row.rowNum] ?? ''} Start time cannot be ahead of end time.`;
            }
        }

        keys.forEach((singleKey: any) => {
            // Get validator object for each key (column.accessors)
            const singleValidator = validators[singleKey] as BulkUploaderColumn['validation'];

            if (singleValidator && Object.prototype.hasOwnProperty.call(singleValidator, 'tests')) {
                // only want to do anything if there are tests present
                if (Array.isArray(singleValidator?.tests)) {
                    const valueToTest = row[singleKey];
                    const updatedMessage = singleValidator?.tests?.reduce(genErrorString(valueToTest), '');

                    if (updatedMessage) {
                        errors[row.rowNum] = `${errors[row.rowNum] ?? ''} ${updatedMessage}`.trim();
                    }
                }
            }
        });
    });

    return Object.entries(errors).reduce((acc, [key, val]) => {
        acc.push({ Row: Number(key), Errors: val });
        return acc;
    }, [] as { Row: Number; Errors: any }[]);
};
